import React, { useState, useEffect } from 'react';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import './AssignComplaintModal.css';
import tenantStorage from '../../utils/tenantStorage';

const AssignComplaintModal = ({ 
  isOpen, 
  onClose, 
  complaint, 
  mode,
  onSuccess 
}) => {
  const [eligibleStaff, setEligibleStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);

  useEffect(() => {
    if (isOpen && complaint) {
      // If mode is assignSelf, fetch current staff details
      if (mode === 'assignSelf' || complaint.mode === 'assignSelf') {
        fetchCurrentStaff();
      } else {
        fetchEligibleStaff();
      }
    }
  }, [isOpen, complaint, mode]);

  const fetchCurrentStaff = async () => {
    setStaffLoading(true);
    try {
      const staffId = tenantStorage.getItem('staffId');
      
      if (!staffId) {
        toastError('Staff ID not found. Please login again.');
        onClose();
        return;
      }

      // Get complaint type from complaint object
      let complaintType = '';
      if (typeof complaint.complaint_type === 'object' && complaint.complaint_type !== null) {
        complaintType = complaint.complaint_type.type_name || complaint.complaint_type.name || '';
      } else {
        complaintType = complaint.complaint_type || '';
      }

      // Fetch current staff using the eligible staff endpoint with staff_id parameter
      const response = await apiGet(`/staff/eligible/${encodeURIComponent(complaintType)}?staff_id=${staffId}`);
      const staffData = response.data || [];
      
      if (staffData.length === 0) {
        toastError('You are not eligible to handle this complaint type.');
        onClose();
        return;
      }
      
      // Add current staff to eligible staff list and pre-select them
      setEligibleStaff(staffData);
      setSelectedStaff(staffData[0]); // Pre-select the current staff
    } catch (error) {
      console.error('Error fetching current staff details:', error);
      toastError('Failed to fetch staff details');
      onClose();
    } finally {
      setStaffLoading(false);
    }
  };

  const fetchEligibleStaff = async () => {
    setStaffLoading(true);
    try {
      // Get complaint type from complaint object
      let complaintType = '';
      if (typeof complaint.complaint_type === 'object' && complaint.complaint_type !== null) {
        complaintType = complaint.complaint_type.type_name || complaint.complaint_type.name || '';
      } else {
        complaintType = complaint.complaint_type || '';
      }

      // Fetch staff members whose staff type contains this complaint type
      const response = await apiGet(`/staff/eligible/${encodeURIComponent(complaintType)}`);
      setEligibleStaff(response.data || []);
    } catch (error) {
      console.error('Error fetching eligible staff:', error);
      toastError('Failed to fetch eligible staff');
      setEligibleStaff([]);
    } finally {
      setStaffLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedStaff) {
      toastError('Please select a staff member to assign');
      return;
    }

    try {
      setLoading(true);

      const updateData = {
        assigned_to: selectedStaff.staff_id
      };

      const response = await apiPost(`/complaints/${complaint.complaint_id}/assign`, updateData);
      
      if (response.status === 200) {
        toastSuccess(`Complaint assigned successfully to ${selectedStaff.name}`);
        onSuccess(); // Refresh the data in parent component
        onClose(); // Close modal
      }
    } catch (error) {
      console.error('Error assigning complaint:', error);
      toastError(error.response?.data?.detail || 'Failed to assign complaint');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedStaff(null);
    onClose();
  };

  const columns = [
    {
      Header: '',
      accessor: 'select',
      Cell: ({ row }) => (
        <input
          type="radio"
          name="staffSelection"
          checked={selectedStaff?.staff_id === row.staff_id}
          onChange={() => setSelectedStaff(row)}
          style={{ cursor: 'pointer' }}
        />
      ),
      width: 50
    },
    {
      Header: 'Staff ID',
      accessor: 'staff_id',
      Cell: ({ value }) => `#${value}`
    },
    {
      Header: 'Name',
      accessor: 'name'
    },
    {
      Header: 'Job Type',
      accessor: 'job_type'
    },
    {
      Header: 'Mobile',
      accessor: 'mobile'
    },
    {
      Header: 'Open Complaints',
      accessor: 'open_complaints_count',
      Cell: ({ value }) => value || 0
    }
  ];

  const footer = (
    <>
      <RTButton
        variant="secondary"
        onClick={handleCancel}
        disabled={loading}
      >
        Cancel
      </RTButton>
      <RTButton
        variant="primary"
        onClick={handleAssign}
        disabled={loading || !selectedStaff}
      >
        {loading ? 'Assigning...' : 'Assign Complaint'}
      </RTButton>
    </>
  );

  const getComplaintTypeDisplay = () => {
    if (typeof complaint?.complaint_type === 'object' && complaint.complaint_type !== null) {
      return complaint.complaint_type.type_name || complaint.complaint_type.name || 'Unknown';
    }
    return complaint?.complaint_type || 'Unknown';
  };

  const getPriorityBadge = (priority) => {
    return (
      <span className={`complaint-priority priority-${priority.toLowerCase()}`}>
        {priority}
      </span>
    );
  };

  return (
    <RTModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={(mode === 'assignSelf' || complaint?.mode === 'assignSelf') ? "Assign Complaint to You" : "Assign Complaint"}
      footer={footer}
      size="large"
    >
      <div className="assign-modal-content">
        <div className="complaint-info">
          <h4>Complaint Details</h4>
          <div className="complaint-info-grid">
            <div className="complaint-info-item">
              <label>Complaint ID:</label>
              <span>#{complaint?.complaint_id || 'N/A'}</span>
            </div>
            <div className="complaint-info-item">
              <label>Type:</label>
              <span>{getComplaintTypeDisplay()}</span>
            </div>
            <div className="complaint-info-item">
              <label>Priority:</label>
              {complaint?.priority ? getPriorityBadge(complaint.priority) : 'N/A'}
            </div>
            <div className="complaint-info-item">
              <label>PG Name:</label>
              <span>{complaint?.pg_name || 'N/A'}</span>
            </div>
          </div>
          <div className="complaint-description">
            <label>Description:</label>
            <p>{complaint?.description || 'N/A'}</p>
          </div>
        </div>

        <div className="staff-selection">
          <h4>{(mode === 'assignSelf' || complaint?.mode === 'assignSelf') ? "Assign to Current Staff" : "Eligible Staff Members"}</h4>
          {staffLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <RTSpinner size="medium" showMessage={true} message="Loading staff details..." />
            </div>
          ) : eligibleStaff.length === 0 ? (
            <div className="no-staff-message">
              <p>{(mode === 'assignSelf' || complaint?.mode === 'assignSelf') ? "Unable to load current staff details. Please try again." : "No eligible staff members found for this complaint type."}</p>
              {!(mode === 'assignSelf' || complaint?.mode === 'assignSelf') && (
                <p>Please ensure staff types are configured with the appropriate complaint types.</p>
              )}
            </div>
          ) : (
            <div className="staff-table-container">
              <Table
                columns={columns}
                data={eligibleStaff}
                loading={false}
                showSearch={false}
                showExport={false}
                exportable={false}
                showPagination={eligibleStaff.length > 10}
                pageSize={10}
              />
              {selectedStaff && (
                <div className="selected-staff-info">
                  <strong>Selected:</strong> {selectedStaff.name} ({selectedStaff.job_type})
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </RTModal>
  );
};

export default AssignComplaintModal;
